import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { partnerAPI } from '../services/api';

// Cyan for the things you did here, green for a sign-in; anything unrecognised stays quiet.
// '' means the tag's own cyan — read with ?? so it isn't mistaken for "no tone".
const ACTION_TONE = {
  TENANT_ACCESS: '',
  PROFILE_UPDATE: '',
  LOGIN: 'pt-tag--ok',
};

export default function ActivityPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    loadActivity();
  }, [limit]);

  const loadActivity = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await partnerAPI.getAuditLog({ limit });
      setEvents(data.events || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="pt-page">
      <Box className="pt-head">
        <Box>
          <p className="pt-eyebrow">Partners</p>
          <h1 className="pt-head__title">Activity log</h1>
          <p className="pt-head__lede">Your recent access history and actions.</p>
        </Box>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Show</InputLabel>
          <Select value={limit} label="Show" onChange={(e) => setLimit(e.target.value)}>
            <MenuItem value={25}>25 items</MenuItem>
            <MenuItem value={50}>50 items</MenuItem>
            <MenuItem value={100}>100 items</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : events.length === 0 ? (
        <Box className="pt-empty">
          <p className="pt-empty__title">No activity yet</p>
          <p>Your access history will appear here.</p>
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((evt, idx) => (
                <TableRow key={evt.sk || idx} hover>
                  <TableCell>
                    <span className={`pt-tag ${ACTION_TONE[evt.action] ?? 'pt-tag--quiet'}`}>
                      {evt.action?.replace(/_/g, ' ') || 'Unknown'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {evt.tenant_subdomain
                      ? `${evt.tenant_subdomain}.elebyte.io`
                      : evt.details?.tenant_subdomain
                        ? `${evt.details.tenant_subdomain}.elebyte.io`
                        : '-'}
                  </TableCell>
                  <TableCell><span className="pt-mono">{evt.ip_address || '-'}</span></TableCell>
                  <TableCell>
                    <span className="pt-mono">
                      {evt.created_at ? new Date(evt.created_at).toLocaleString() : '-'}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
